import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, Grid, Paper, Typography } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import ChairAltIcon from "@mui/icons-material/ChairAlt";
import api from "../../api";
import { toast } from "react-toastify";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import AnalyticsFiltersBar from "./AnalyticsFiltersBar";
import useAnalyticsFilters from "./useAnalyticsFilters";
import AnalyticsDataTable, { StatusChip } from "./AnalyticsDataTable";

const TILE_CONFIGS = [
  { key: "total_batches",    label: "Total Groups",   Icon: GroupsIcon,   iconBg: "#ede9fe", iconColor: "#7c3aed" },
  { key: "seats_filled",     label: "Seats Filled",    Icon: PersonIcon,   iconBg: "#dcfce7", iconColor: "#16a34a" },
  { key: "seats_left",       label: "Seats Left",      Icon: ChairAltIcon, iconBg: "#fff7ed", iconColor: "#ea580c" },
];

function KpiTile({ label, value, Icon, iconBg, iconColor }) {
  return (
    <Paper sx={{ ...CARD_SX, display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ color: iconColor, fontSize: 26 }} />
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{(value ?? 0).toLocaleString()}</Typography>
      </Box>
    </Paper>
  );
}

function ModeChip({ mode }) {
  const isOnline = String(mode || "").toLowerCase() === "online";
  return (
    <Chip
      label={isOnline ? "Online" : "In Person"}
      size="small"
      sx={{ bgcolor: isOnline ? "#dbeafe" : "#fff7ed", color: isOnline ? "#2563eb" : "#ea580c", fontWeight: 600 }}
    />
  );
}

export default function BatchCapacityAnalytics() {
  const [courseId, setCourseId] = useState("");
  const [academicCourseId, setAcademicCourseId] = useState("");
  const [planId, setPlanId] = useState("");
  const [location, setLocation] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const { courses, academicCourses, plans, locations } = useAnalyticsFilters({ courseId, academicCourseId });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (courseId) params.course_id = courseId;
      if (academicCourseId) params.academic_course_id = academicCourseId;
      if (planId) params.course_plan_id = planId;
      if (location) params.location = location;
      const res = await api.get("admin/analytics/batch-capacity", { params });
      setRows(res.data?.data?.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load batch capacity");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [courseId, academicCourseId, planId, location]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => ({
    total_batches: rows.length,
    seats_filled: rows.reduce((s, r) => s + (r.filled_seats || 0), 0),
    seats_left: rows.reduce((s, r) => s + (r.remaining_seats || 0), 0),
  }), [rows]);

  const columns = [
    { key: "batch_name", label: "Group Name" },
    { key: "mode", label: "Mode", render: (r) => <ModeChip mode={r.mode} /> },
    { key: "location", label: "Location" },
    { key: "capacity", label: "Capacity", render: (r) => r.capacity ?? "—" },
    { key: "filled_seats", label: "Filled" },
    { key: "remaining_seats", label: "Left", render: (r) => r.remaining_seats ?? "—" },
    { key: "assigned_tutors", label: "Tutor", render: (r) => (r.assigned_tutors?.length ? r.assigned_tutors.join(", ") : "—") },
    { key: "helper_count", label: "Helper" },
    { key: "status", label: "Status", render: (r) => <StatusChip status={r.status} /> },
  ];

  return (
    <AnalyticsPageShell
      title="Group Capacity & Staff"
      subtitle="Simple overview of seat availability and staff for each group."
      filters={
        <AnalyticsFiltersBar
          courses={courses}
          academicCourses={academicCourses}
          plans={plans}
          locations={locations}
          courseId={courseId}
          setCourseId={setCourseId}
          showAcademicCourse={false}
          academicCourseId={academicCourseId}
          setAcademicCourseId={setAcademicCourseId}
          planId={planId}
          setPlanId={setPlanId}
          showLocation
          location={location}
          setLocation={setLocation}
        />
      }
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {TILE_CONFIGS.map(({ key, label, Icon, iconBg, iconColor }) => (
          <Grid item xs={12} sm={4} key={key}>
            <KpiTile label={label} value={summary[key]} Icon={Icon} iconBg={iconBg} iconColor={iconColor} />
          </Grid>
        ))}
      </Grid>
      <AnalyticsDataTable columns={columns} rows={rows} loading={loading} />
    </AnalyticsPageShell>
  );
}
